import { useUnitSystem, formatWeight } from "@/lib/units";
import { FaqBlock } from "@/components/FaqBlock";
import {
  buildCurbWeightPage,
  type BuildInput,
  type CurbWeightPage,
} from "@/lib/curb-weight-rebuild";

type Props = Omit<BuildInput, "fmt">;

/** Build the page model with the reader's chosen unit system. */
export function useCurbWeightPage(props: Props): CurbWeightPage | null {
  const [unit] = useUnitSystem();
  return buildCurbWeightPage({ ...props, fmt: (lbs) => formatWeight(lbs, unit) });
}

/**
 * Renders only the modules this vehicle has data for, in the order the data
 * itself dictates (largest explained weight difference first).
 */
export function CurbWeightRebuild(props: Props) {
  const page = useCurbWeightPage(props);
  if (!page) return null;

  return (
    <>
      {page.modules.map((m) => (
        <section
          key={m.id}
          className="mx-auto max-w-6xl px-4 sm:px-6 py-8 border-t border-border"
          data-module={m.id}
        >
          <h2 className="text-xl font-bold text-foreground">{m.heading}</h2>
          <div className="mt-3 space-y-3 max-w-3xl text-sm text-muted-foreground leading-relaxed">
            {m.paragraphs.map((p, i) => (
              <p key={i}>{p}</p>
            ))}
          </div>
          {m.rows && m.rows.length > 0 && (
            <div className="mt-4 overflow-x-auto rounded-lg border border-border bg-card">
              <table className="w-full text-sm">
                <tbody>
                  {m.rows.map((row, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 text-foreground">{row.label}</td>
                      <td className="px-4 py-2 font-semibold text-foreground whitespace-nowrap">
                        {row.value}
                      </td>
                      <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">
                        {row.note ?? ""}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      ))}

      <section className="mx-auto max-w-6xl px-4 sm:px-6 py-6 border-t border-border">
        <p className="text-xs text-muted-foreground">{page.sourceLine}</p>
      </section>

      {page.faqs.length > 0 && (
        <section className="mx-auto max-w-3xl px-4 sm:px-6 py-10 border-t border-border">
          <h2 className="text-xl font-bold text-foreground mb-4">
            Questions these figures answer
          </h2>
          <FaqBlock items={page.faqs} />
        </section>
      )}
    </>
  );
}
